import { CommonModule, NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    inject,
    OnDestroy,
    OnInit,
    Signal,
} from '@angular/core';
import {
    AbstractControl,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { IMaskModule } from 'angular-imask';
import {
    forkJoin,
    map,
    merge,
    Observable,
    of,
    switchMap,
    take,
    takeUntil,
    tap,
} from 'rxjs';
import { IMaskConstants } from '../../../../core/constants/imask';
import { RouteConstants } from '../../../../core/constants/routes';
import { UserRole } from '../../../../core/enums/users/role';
import {
    nameOf,
    padFrnWithLeadingZeros,
} from '../../../../core/functions/string-helpers';
import { BlockNavigationIfChange } from '../../../../core/models/block-navigation-if-change';
import { DropdownOption } from '../../../../core/models/dropdown-option';
import { FormGroupType } from '../../../../core/models/form-group-type';
import { RegExPattern } from '../../../../core/models/regexp-pattern/regex-pattern';
import { DropdownService } from '../../../../core/services/resources/dropdown.service';
import { FrnEntitySearchResponse } from '../../../../core/services/uls/models/frn-entity-search-response';
import { ApplicationFormFieldComponent } from '../../../../shared/components/fcc-601/application-form-field/application-form-field.component';
import { ApplicationPageBaseComponent } from '../../../../shared/components/fcc-601/application-page-base/application-page-base.component';
import { PhoneNumberInputComponent } from '../../../../shared/components/form-components/phone-number-input/phone-number-input.component';
import { RegexInputDirective } from '../../../../shared/directives/regex-input/regex-input.directive';
import { SelectTypeaheadDirective } from '../../../../shared/directives/select-typeahead/select-typeahead.directive';
import { UserStateService } from '../../../user/shared/services/user-state/user-state.service';
import { FrnFilingCreationRequest } from '../../frn-filing/shared/models/frn-filing-creation-request';
import { FrnFilingService } from '../../frn-filing/shared/services/frn-filing.service';
import { FrnSearchDialogComponent } from '../../shared/components/frn-search-dialog/frn-search-dialog.component';
import { ApplicationPurposeCodeEnum } from '../../shared/constants/application-purpose-codes';
import { StepValidationResult } from '../../shared/models/step-validation-result';
import { AttachmentRequiredButtonComponent } from '../shared/components/attachment/attachment-required-button/attachment-required-button.component';
import { ApplicationPageFacadeService } from '../shared/services/application-page-facade/application-page-facade.service';
import { DirectEntryService } from '../shared/services/direct-entry/direct-entry.service';
import { FccLicensingInfoConsentDialogComponent } from './components/fcc-licensing-info-consent-dialog/fcc-licensing-info-consent-dialog.component';
import { ApplicantInfoMessages } from './constants/applicant-info-messages';
import { ApplicantTypeCodes } from './constants/applicant-type-codes';
import { ApplicantInfoRequest } from './models/applicant-info-request';
import { ApplicantInfoResponse } from './models/applicant-info-response';

@Component({
    selector: 'app-applicant-info',
    templateUrl: './applicant-info.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MatSelectModule,
        MatFormFieldModule,
        MatInputModule,
        FormsModule,
        ReactiveFormsModule,
        MatTooltipModule,
        MatIconModule,
        ApplicationFormFieldComponent,
        MatCheckboxModule,
        RegexInputDirective,
        PhoneNumberInputComponent,
        IMaskModule,
        NgClass,
        AttachmentRequiredButtonComponent,
        MatButtonModule,
        CommonModule,
        SelectTypeaheadDirective,
    ],
})
export class ApplicantInfoComponent
    extends ApplicationPageBaseComponent<
        ApplicantInfoRequest,
        ApplicantInfoResponse,
        ApplicantInfoResponse
    >
    implements OnInit, OnDestroy, BlockNavigationIfChange
{
    maskOptions = IMaskConstants;
    regExPattern = RegExPattern;
    messages: typeof ApplicantInfoMessages = ApplicantInfoMessages;

    yesNoOptions: DropdownOption[] = [];
    applicantTypeOptions: DropdownOption[] = [];
    stateCodeOptions: DropdownOption[] = [];

    fccLicensingInfoModified: boolean = false;

    isApplicationTemplate: Signal<boolean> =
        this.applicationPageFacadeService.applicationStateService
            .IsApplicationTemplate;

    get isCustomerRole(): boolean {
        return this.userStateService.getRole() === UserRole.Customer;
    }

    /**
     * Mapping between the licensee and contact information fields.
     */
    readonly applicantAndContactInformationMapping: {
        [key in keyof ApplicantInfoResponse]?: keyof ApplicantInfoResponse;
    } = {
        licenseeFirstName: 'contactFirstName',
        licenseeMiddleInitial: 'contactMiddleInitial',
        licenseeLastName: 'contactLastName',
        licenseeSuffix: 'contactSuffix',
        licenseeEntityName: 'contactEntityName',
        licenseeAttentionTo: 'contactAttentionTo',
        licenseePoBox: 'contactPoBox',
        licenseeStreetAddress: 'contactStreetAddress',
        licenseeCity: 'contactCity',
        licenseeStateProvinceInitials: 'contactStateProvinceInitials',
        licenseePostalCode: 'contactPostalCode',
        licenseePhoneNumber: 'contactPhoneNumber',
        licenseeFaxNumber: 'contactFaxNumber',
        licenseeEmailAddress: 'contactEmailAddress',
    };

    /**
     * Fields that are part of the Contact Information section of this step. If any of these steps are modified, then
     * the user will be prompted, upon navigation away from the step, to consent to have their information be saved to their
     * CiviCRM Contact record.
     */
    readonly fccLicensingInformationFields: Array<keyof ApplicantInfoResponse> =
        [
            'contactFirstName',
            'contactMiddleInitial',
            'contactLastName',
            'contactSuffix',
            'contactEntityName',
            'contactAttentionTo',
            'contactPoBox',
            'contactStreetAddress',
            'contactCity',
            'contactStateProvinceInitials',
            'contactPostalCode',
            'contactPhoneNumber',
            'contactFaxNumber',
            'contactEmailAddress',
        ];

    override form: FormGroup<FormGroupType<ApplicantInfoResponse>> =
        new FormGroup<FormGroupType<ApplicantInfoResponse>>({
            fccRegistrationNumber: new FormControl<string | null>(
                null,
                Validators.required,
            ),
            applicantTypeCode: new FormControl<string | null>(
                null,
                Validators.required,
            ),
            licenseeNameChange: new FormControl<boolean | null>(null),
            licenseeFirstName: new FormControl<string | null>(null),
            licenseeMiddleInitial: new FormControl<string | null>(null),
            licenseeLastName: new FormControl<string | null>(null),
            licenseeSuffix: new FormControl<string | null>(null),
            licenseeEntityName: new FormControl<string | null>(null),
            licenseeAttentionTo: new FormControl<string | null>(null),
            licenseePoBox: new FormControl<string | null>(
                null,
                Validators.required,
            ),
            licenseeStreetAddress: new FormControl<string | null>(
                null,
                Validators.required,
            ),
            licenseeCity: new FormControl<string | null>(
                null,
                Validators.required,
            ),
            licenseeStateProvinceInitials: new FormControl<string | null>(
                null,
                Validators.required,
            ),
            licenseePostalCode: new FormControl<string | null>(
                null,
                Validators.required,
            ),
            licenseePhoneNumber: new FormControl<string | null>(
                null,
                Validators.required,
            ),
            licenseeFaxNumber: new FormControl<string | null>(null),
            licenseeEmailAddress: new FormControl<string | null>(
                null,
                Validators.required,
            ),
            realPartyEntityName: new FormControl<string | null>(null),
            realPartyFccRegistratonNumber: new FormControl<string | null>(null),
            isContactSameAsApplicant: new FormControl<boolean>(false),
            hasEmailBookedNotification: new FormControl<boolean>(false),
            contactFirstName: new FormControl<string | null>(
                null,
                Validators.required,
            ),
            contactMiddleInitial: new FormControl<string | null>(null),
            contactLastName: new FormControl<string | null>(
                null,
                Validators.required,
            ),
            contactSuffix: new FormControl<string | null>(null),
            contactEntityName: new FormControl<string | null>(
                null,
                Validators.required,
            ),
            contactAttentionTo: new FormControl<string | null>(
                null,
                Validators.required,
            ),
            contactPoBox: new FormControl<string | null>(
                null,
                Validators.required,
            ),
            contactStreetAddress: new FormControl<string | null>(
                null,
                Validators.required,
            ),
            contactCity: new FormControl<string | null>(
                null,
                Validators.required,
            ),
            contactStateProvinceInitials: new FormControl<string | null>(
                null,
                Validators.required,
            ),
            contactPostalCode: new FormControl<string | null>(
                null,
                Validators.required,
            ),
            contactPhoneNumber: new FormControl<string | null>(
                null,
                Validators.required,
            ),
            contactFaxNumber: new FormControl<string | null>(null),
            contactEmailAddress: new FormControl<string | null>(
                null,
                Validators.required,
            ),
        });

    private readonly directEntryService = inject(DirectEntryService);
    private readonly dropdownService = inject(DropdownService);
    private readonly dialog = inject(MatDialog);
    private readonly frnFilingService = inject(FrnFilingService);
    private readonly userStateService = inject(UserStateService);

    constructor() {
        super(
            inject(ActivatedRoute),
            inject(Router),
            inject(ApplicationPageFacadeService),
        );
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.loadDropdownData();
        this.setupFormHandlers();
    }

    protected override postFormBindingActions(): void {
        this.setupPostFormBindingFormHandlers();
        this.disableLicenseeNameChangeForNewApps();
        this.applyFieldToggleRules();
    }

    private setupPostFormBindingFormHandlers(): void {
        this.form.controls.isContactSameAsApplicant?.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe((sameAsApplicant) => {
                for (const key in this.applicantAndContactInformationMapping) {
                    const contactField = this
                        .applicantAndContactInformationMapping[
                        key as keyof ApplicantInfoResponse
                    ] as keyof ApplicantInfoResponse;
                    if (sameAsApplicant) {
                        this.form.controls[contactField]?.setValue(
                            this.form.get(key)?.value,
                        );
                    } else {
                        this.form.controls[contactField]?.reset();
                    }
                }
            });

        if (!this.isApplicationTemplate()) {
            this.fccLicensingInformationFields.forEach((field) => {
                this.form
                    .get(field)
                    ?.valueChanges.pipe(takeUntil(this.destroy$))
                    .subscribe(() => {
                        this.fccLicensingInfoModified = true;
                    });
            });
        }
    }

    private setupFormHandlers() {
        this.form.controls.applicantTypeCode.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe((value) => {
                this.updateApplicantValidationRules(value);
            });

        this.getEitherOrRequiredFieldMaps().forEach(({ groupA, groupB }) => {
            const controls: AbstractControl[] = [...groupA, ...groupB].map(
                (field) => this.form.controls[field],
            );

            merge(...controls.map((control) => control.valueChanges))
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => this.applyEitherOrValidation(groupA, groupB));
        });
    }

    private loadDropdownData() {
        forkJoin({
            yesNoOptions: this.dropdownService.getYesNoOptions(),
            applicantTypeOptions: this.dropdownService.getApplicantTypeCodes(),
            stateCodeOptions: this.dropdownService.getStateCodes(),
        })
            .pipe(
                takeUntil(this.destroy$),
                tap(
                    ({
                        yesNoOptions: yesNo,
                        applicantTypeOptions: applicantTypeList,
                        stateCodeOptions: stateCodeList,
                    }) => {
                        this.yesNoOptions = yesNo;
                        this.applicantTypeOptions = applicantTypeList;
                        this.stateCodeOptions = stateCodeList;
                    },
                ),
            )
            .subscribe();
    }

    /**
     * Requires either first/last name OR entity name depending on the @param applicantTypeCode.
     * First/last is required for individual, entity name is required for all other selections.
     * @param applicantTypeCode from the form.
     */
    private updateApplicantValidationRules(
        applicantTypeCode: string | null,
    ): void {
        const licenseeFirstNameControl = this.form.get(
            nameOf<ApplicantInfoResponse>('licenseeFirstName'),
        );
        const licenseeLastNameControl = this.form.get(
            nameOf<ApplicantInfoResponse>('licenseeLastName'),
        );
        const licenseeEntityNameControl = this.form.get(
            nameOf<ApplicantInfoResponse>('licenseeEntityName'),
        );

        if (!applicantTypeCode) {
            return;
        }

        if (applicantTypeCode === ApplicantTypeCodes.individual) {
            licenseeFirstNameControl?.addValidators(Validators.required);
            licenseeLastNameControl?.addValidators(Validators.required);
            licenseeEntityNameControl?.removeValidators(Validators.required);
        } else {
            licenseeFirstNameControl?.removeValidators(Validators.required);
            licenseeLastNameControl?.removeValidators(Validators.required);
            licenseeEntityNameControl?.addValidators(Validators.required);
        }

        licenseeFirstNameControl?.updateValueAndValidity();
        licenseeLastNameControl?.updateValueAndValidity();
        licenseeEntityNameControl?.updateValueAndValidity();
    }

    private applyFieldToggleRules(): void {
        this.getEitherOrRequiredFieldMaps().forEach(({ groupA, groupB }) =>
            this.applyEitherOrValidation(groupA, groupB),
        );
    }

    private getEitherOrRequiredFieldMaps(): Array<{
        groupA: Array<keyof FormGroupType<ApplicantInfoResponse>>;
        groupB: Array<keyof FormGroupType<ApplicantInfoResponse>>;
    }> {
        return [
            {
                groupA: [nameOf<ApplicantInfoResponse>('licenseePoBox')],
                groupB: [
                    nameOf<ApplicantInfoResponse>('licenseeStreetAddress'),
                ],
            },
            {
                groupA: [
                    nameOf<ApplicantInfoResponse>('contactFirstName'),
                    nameOf<ApplicantInfoResponse>('contactLastName'),
                ],
                groupB: [nameOf<ApplicantInfoResponse>('contactAttentionTo')],
            },
            {
                groupA: [nameOf<ApplicantInfoResponse>('contactPoBox')],
                groupB: [nameOf<ApplicantInfoResponse>('contactStreetAddress')],
            },
        ] as Array<{
            groupA: Array<keyof FormGroupType<ApplicantInfoResponse>>;
            groupB: Array<keyof FormGroupType<ApplicantInfoResponse>>;
        }>;
    }

    /**
     * Sets up either-or required validation for alternative form-control groups.
     */
    private applyEitherOrValidation(
        groupA: Array<keyof FormGroupType<ApplicantInfoResponse>>,
        groupB: Array<keyof FormGroupType<ApplicantInfoResponse>>,
    ): void {
        const controlsA = groupA.map((field) => this.form.controls[field]);
        const controlsB = groupB.map((field) => this.form.controls[field]);

        const groupAHasValue = controlsA.every((control) => !!control.value);
        const groupBHasValue = controlsB.every((control) => !!control.value);

        this.setRequired(controlsA, !groupBHasValue);
        this.setRequired(controlsB, !groupAHasValue);
    }

    private setRequired(controls: AbstractControl[], required: boolean): void {
        controls.forEach((control) => {
            if (required) {
                control.addValidators(Validators.required);
            } else {
                control.removeValidators(Validators.required);
            }
            control.updateValueAndValidity({ emitEvent: false });
        });
    }

    private disableLicenseeNameChangeForNewApps(): void {
        const control = this.form.controls.licenseeNameChange;

        if (
            this.applicationState?.applicationPurpose ===
            ApplicationPurposeCodeEnum.new
        ) {
            this.applicationPageFacadeService.applicationFormControlManager.disable(
                control,
                { emitEvent: false },
            );
        } else {
            this.applicationPageFacadeService.applicationFormControlManager.enable(
                control,
                { emitEvent: false },
            );
        }
    }

    /**
     * Whether this is a NEW application (only new applications should show the Request/Search FRN buttons).
     */
    get isNewApplication(): boolean {
        return (
            this.applicationState?.applicationPurpose ===
            ApplicationPurposeCodeEnum.new
        );
    }

    /**
     * Opens the FRN search dialog.
     */
    openFrnSearchDialog(): void {
        if (this.dialog.openDialogs.length > 0) {
            return;
        }

        const dialogRef = this.dialog.open(FrnSearchDialogComponent, {
            width: '1300px',
            disableClose: true,
            data: {
                frn: this.form.controls.fccRegistrationNumber.value,
            },
        });

        dialogRef
            .afterClosed()
            .pipe(take(1))
            .subscribe((result?: FrnEntitySearchResponse) => {
                if (result) {
                    this.applyFrnSearchResultToForm(result);
                }
            });
    }

    /**
     * Maps FrnEntitySearchResponse to form fields 13-26.
     * Clears and overwrites all values with the returned data.
     */
    private applyFrnSearchResultToForm(result: FrnEntitySearchResponse): void {
        // Mark the form dirty before patching so patchValue's valueChanges event
        // is handled as a change by the base form listener.
        this.form.markAsDirty();

        // Apply the FRN search result to the form fields.
        this.form.patchValue({
            fccRegistrationNumber: result.frnChar,
            applicantTypeCode: result.applicantTypeCode,
            licenseeNameChange: null,
            licenseeFirstName: result.firstName,
            licenseeMiddleInitial: result.mi,
            licenseeLastName: result.lastName,
            licenseeSuffix: result.suffix,
            licenseeEntityName: result.entityName,
            licenseeAttentionTo: result.attn,
            licenseePoBox: result.poBox,
            licenseeStreetAddress: result.streetAddress,
            licenseeCity: result.city,
            licenseeStateProvinceInitials: result.state,
            licenseePostalCode: result.zip,
            licenseePhoneNumber: result.phoneNum,
            licenseeFaxNumber: result.faxNum,
            licenseeEmailAddress: result.email,
        });
    }

    /**
     * Handles the Request/View FRN button click. Saves current form data if needed,
     * then navigates to an existing FRN filing (if it exists) or a newly created one.
     */
    onFrnRequestOrViewClick(): void {
        this.handleFrnRequestOrView()
            .pipe(take(1))
            .subscribe({
                next: (applicationId) =>
                    this.navigateToFrnFiling(applicationId),
                error: (err) => {
                    console.error('FRN filing operation failed:', err);
                },
            });
    }

    /**
     * Orchestrates the save and create/navigate flow for FRN filing.
     * @returns Observable emitting the application ID to navigate to
     */
    private handleFrnRequestOrView(): Observable<string> {
        // Only save if there are changes AND not in read-only mode
        const saveAction$ =
            this.hasChanges && !this.isReadOnly()
                ? this.saveFormDataToApi(this.generateRequestObject()).pipe(
                      tap(() => {
                          this.hasChanges = false;
                      }),
                      map(() => void 0),
                  )
                : of(void 0);

        return saveAction$.pipe(
            switchMap(() => {
                // If FRN filing already exists, return current application ID
                if (this.applicationState?.hasFrnRequest) {
                    return of(this.applicationId!);
                }

                // Otherwise create new FRN filing and return its ID
                const createRequest: FrnFilingCreationRequest = {
                    coresRegistrationType: null,
                    hasUsaContactAddress: null,
                    parentApplicationId: Number(this.applicationId),
                };

                return this.frnFilingService
                    .createFrnFiling(createRequest)
                    .pipe(map((response) => response.applicationId.toString()));
            }),
        );
    }

    private navigateToFrnFiling(applicationId: string): void {
        this.router.navigate([
            RouteConstants.FrnFiling.getFullRouteWithApplicationId(
                'typeOfFiling',
                applicationId,
            ),
        ]);
    }

    private openFccLicensingInfoConsentDialog(): Observable<boolean> {
        if (this.dialog.openDialogs.length > 0) {
            return of(false);
        }

        const dialogRef = this.dialog.open(
            FccLicensingInfoConsentDialogComponent,
            {
                disableClose: true,
            },
        );

        return dialogRef.afterClosed() as Observable<boolean>;
    }

    protected override bindForm(response: ApplicantInfoRequest): void {
        response.fccRegistrationNumber = padFrnWithLeadingZeros(
            response.fccRegistrationNumber,
        );

        response.realPartyFccRegistratonNumber = padFrnWithLeadingZeros(
            response.realPartyFccRegistratonNumber,
        );

        this.form.patchValue(response);

        if (response.hasEmailBookedNotification === null) {
            this.form.controls.hasEmailBookedNotification.setValue(true);

            this.hasChanges = true;
        } else if (response.hasEmailBookedNotification === true) {
            this.applicationPageFacadeService.applicationFormControlManager.disable(
                this.form.controls.hasEmailBookedNotification,
                { emitEvent: false },
            );
        }
    }

    protected override getFormDataFromApi(
        applicationId: string,
    ): Observable<ApplicantInfoResponse> {
        return this.directEntryService.getApplicantInfo(applicationId);
    }

    protected override saveFormDataToApi(
        request: ApplicantInfoRequest,
    ): Observable<StepValidationResult> {
        /**
         * If the Contact Information has been modified, we need to show the consent dialog to ask
         * the user for permission to save their modified information to their CiviCRM Contact record.
         */
        const resolveRequest$ = this.fccLicensingInfoModified
            ? this.openFccLicensingInfoConsentDialog().pipe(
                  map((consentStatus: boolean) => ({
                      ...request,
                      shouldSaveFccLicensingInfoToCiviCrm: consentStatus,
                  })),
              )
            : of(request);

        return resolveRequest$.pipe(
            switchMap((resolvedRequest) =>
                this.directEntryService.saveApplicantInfo(resolvedRequest),
            ),
        );
    }

    protected override generateRequestObject(): ApplicantInfoRequest {
        const formValue =
            this.form.getRawValue() as Required<ApplicantInfoRequest>;

        return {
            ...formValue,
            applicationId: this.applicationId!,
        };
    }
}
